import { Button } from "@/components/ui/button";
import { useNavigate, Link } from "react-router-dom";
import { useEffect } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { Card } from "@/components/ui/card";
import Footer from "@/components/Footer";
import LandingNav from "@/components/LandingNav";
import SEOHead from "@/components/SEOHead";
import { useTranslation } from "react-i18next";
import { Capacitor } from "@capacitor/core";
import pasteDemo from "@/assets/paste-demo.gif";
import { 
  Zap,
  Shield,
  Rocket,
  Play,
  Users,
  Chrome,
  Monitor,
  Apple,
  Smartphone,
  Code,
  ArrowDownUp,
  FolderTree,
  UserCheck,
  Gift,
  UserPlus,
  Database,
  Globe,
  Bot
} from "lucide-react";

// Detect if running inside Electron
const isElectron =
  typeof window !== "undefined" &&
  (window.electronAPI !== undefined || window.navigator.userAgent.includes("Electron"));

const Landing = () => {
  const navigate = useNavigate();
  const { user, loading } = useAuth();
  const { t } = useTranslation();

  // Redirect based on auth state
  useEffect(() => {
    if (loading) return;
    
    if (user) {
      // Authenticated users go to the app
      navigate('/app', { replace: true });
    } else if (isElectron) {
      // In Electron, unauthenticated users go straight to login
      navigate('/auth', { replace: true });
    } else if (Capacitor.isNativePlatform()) {
      // On mobile native apps, unauthenticated users go to login
      navigate('/auth', { replace: true });
    }
  }, [loading, user, navigate]);

  const features = [
    {
      icon: Monitor,
      title: t("landing.features.platformIntegrations.title"),
      description: t("landing.features.platformIntegrations.description"),
      platformIcons: [Chrome, Monitor, Apple, Smartphone],
    },
    {
      icon: Code,
      title: t("landing.features.restApi.title"),
      description: t("landing.features.restApi.description"),
    },
    {
      icon: Bot,
      title: t("landing.features.mcpServer.title"),
      description: t("landing.features.mcpServer.description"),
    },
    {
      icon: Users,
      title: t("landing.features.teamCollaboration.title"),
      description: t("landing.features.teamCollaboration.description"),
    },
    {
      icon: ArrowDownUp,
      title: t("landing.features.importExport.title"),
      description: t("landing.features.importExport.description"),
    },
    {
      icon: FolderTree,
      title: t("landing.features.nestedAssets.title"),
      description: t("landing.features.nestedAssets.description"),
    },
    {
      icon: UserCheck,
      title: t("landing.features.sharingPermissions.title"),
      description: t("landing.features.sharingPermissions.description"),
    },
    {
      icon: Gift,
      title: t("landing.features.freePilot.title"),
      description: t("landing.features.freePilot.description"),
    },
    {
      icon: Shield,
      title: t("landing.features.manualAutomatedTesting.title"),
      description: t("landing.features.manualAutomatedTesting.description"),
    },
  ];

  return (
    <div className="min-h-screen bg-background flex flex-col">
      <SEOHead
        title="TestAssets – Manage & Paste Test Data Instantly"
        description="Free test data management tool for QA teams. Organize, share, and paste test assets across browser extensions, desktop apps, and REST APIs."
        path="/"
        jsonLd={[
          {
            "@context": "https://schema.org",
            "@type": "SoftwareApplication",
            name: "TestAssets",
            applicationCategory: "DeveloperApplication",
            operatingSystem: "Windows, macOS, Android, iOS, Web",
            offers: { "@type": "Offer", price: "0", priceCurrency: "EUR" },
            description: "Free test data management tool for QA teams.",
            url: "https://testassets.io",
          },
          {
            "@context": "https://schema.org",
            "@type": "WebSite",
            url: "https://testassets.io",
            name: "TestAssets",
            potentialAction: {
              "@type": "SearchAction",
              target: "https://testassets.io/?q={search_term_string}",
              "query-input": "required name=search_term_string",
            },
          },
        ]}
      />
      <LandingNav />

      <main>
        {/* Hero Section with Gradient */}
        <section className="relative overflow-hidden" aria-labelledby="hero-heading">
        <div 
          className="absolute inset-0 opacity-90"
          style={{
            background: 'linear-gradient(135deg, hsl(0 40% 45%), hsl(250 50% 40%))',
          }}
        />
        <div className="relative container mx-auto px-6 py-24 md:py-32">
          <div className="max-w-4xl mx-auto text-center space-y-8">
            {/* Badge */}
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-background/20 backdrop-blur-sm border border-white/10">
              <Rocket className="h-4 w-4 text-white" />
              <span className="text-sm text-white">{t("landing.badge")}</span>
            </div>

            {/* Headline */}
            <h1 id="hero-heading" className="text-5xl md:text-6xl lg:text-7xl font-bold text-white leading-tight">
              {t("landing.headline")}
            </h1>

            {/* Description */}
            <p className="text-xl md:text-2xl text-white/80 max-w-2xl mx-auto">
              {t("landing.description")}
            </p>

            {/* CTA Buttons */}
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4 pt-4">
              <Button 
                size="lg"
                variant="primary"
                asChild
                className="rounded-lg text-lg px-8 py-6"
              >
                <Link to="/auth">
                  <Rocket className="h-5 w-5" />
                  {t("landing.startFreeTrial")}
                </Link>
              </Button>
              <Button 
                size="lg"
                variant="outline"
                asChild
                className="rounded-lg text-lg px-8 py-6 bg-white/10 hover:bg-white/20 text-white border-white/20"
              >
                <Link to="/how-it-works">
                  <Play className="h-5 w-5" />
                  {t("landing.howItWorksButton", "How It Works")}
                </Link>
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* Paste Demo Section */}
      <section className="py-24 bg-card" aria-labelledby="demo-heading">
        <div className="container mx-auto px-6">
          <div className="text-center mb-12">
            <h2 id="demo-heading" className="text-3xl md:text-4xl font-bold text-foreground mb-4">
              {t("howItWorks.demoTitle", "Paste Test Data in Seconds")}
            </h2>
            <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
              {t("howItWorks.demoDescription", "See how easy it is to paste your test data from your TestAssets Asset list directly into your application.")}
            </p>
          </div>

          <div className="max-w-4xl mx-auto">
            <img
              src={pasteDemo}
              alt="Demo showing the test data pasting process — copying rows from a spreadsheet and pasting them as assets into a list"
              className="w-full rounded-xl border border-border shadow-lg"
              loading="lazy"
              width={800}
              height={450}
            />
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-24 bg-background" aria-labelledby="features-heading">
        <div className="container mx-auto px-6">
          <div className="text-center mb-16">
            <h2 id="features-heading" className="text-4xl md:text-5xl font-bold text-foreground mb-4">
              {t("landing.featuresTitle")}
            </h2>
            <p className="text-xl text-muted-foreground max-w-3xl mx-auto">
              {t("landing.featuresDescription")}
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 max-w-7xl mx-auto">
            {features.map((feature, index) => {
              const Icon = feature.icon;
              return (
                <Card 
                  key={index}
                  className="p-8 bg-card border-border hover:border-primary/50 transition-all duration-300 hover:shadow-lg"
                >
                  <div className="flex flex-col gap-4">
                    <div className="h-12 w-12 rounded-lg bg-primary/10 flex items-center justify-center">
                      <Icon className="h-6 w-6 text-primary" />
                    </div>
                    <h3 className="text-xl font-semibold text-card-foreground">
                      {feature.title}
                    </h3>
                    <p className="text-muted-foreground leading-relaxed">
                      {feature.description}
                    </p>
                    {feature.platformIcons && (
                      <div className="flex items-center gap-3 pt-2">
                        {feature.platformIcons.map((PlatformIcon, i) => (
                          <PlatformIcon key={i} className="h-5 w-5 text-muted-foreground" />
                        ))}
                      </div>
                    )}
                  </div>
                </Card>
              );
            })}
          </div>
        </div>
        </section>
      </main>

      <Footer />
    </div>
  );
};

export default Landing;
